"use client"

/**
 * Creator onboarding.
 *
 * Two things were broken here and both are fixed by moving work to the server:
 *
 *   1. Submit fetched http://127.0.0.1:5000 from the BROWSER. In a browser
 *      127.0.0.1 is the visitor's own machine, so this only ever worked on a
 *      developer laptop with uvicorn running — everyone else got
 *      "TypeError: Failed to fetch". Now posts same-origin to
 *      /api/creator/onboard.
 *
 *   2. The username check queried public.creators directly. RLS on that table
 *      is `auth.uid() = user_id`, so the browser sees only its own row;
 *      `.neq("user_id", me)` then removed it, so the query always returned
 *      nothing and every username looked available. Now asks
 *      /api/creator/username-available, which can see all rows.
 */

import { useCallback, useEffect, useState } from "react"
import { Modal } from "@/components/ui/modal"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "sonner"
import { Loader2, X, User, Globe } from "lucide-react"
import { Checkmark as Check } from '@/components/ui/checkmark'
import { cn } from "@/lib/utils"
import { useRouter } from "next/navigation"

interface OnboardingModalProps {
  isOpen: boolean
  onClose: () => void
}

type UsernameStatus = "idle" | "checking" | "available" | "taken" | "invalid"

/** Matches USERNAME_RE in the availability route. */
const USERNAME_RE = /^[a-zA-Z0-9_.]{3,30}$/

const errorMessage = (e: unknown): string | null => (e instanceof Error ? e.message : null)

export function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    channel_name: "",
    email: "",
  })

  /*
    Availability is stored WITH the username it describes, and the visible
    status is derived from whether that matches what is currently typed. That
    makes "checking" a derived state rather than something the effect has to
    set, so there is no synchronous setState in the effect body and no window
    where a stale "available" is shown for freshly-typed text.
  */
  const [serverCheck, setServerCheck] = useState<{
    username: string
    status: "available" | "taken" | "unknown"
    reason: string | null
  } | null>(null)

  /** Shape check that does not need the server. */
  const localValidity: "empty" | "too-short" | "invalid" | "ok" = !formData.username
    ? "empty"
    : formData.username.length < 3
      ? "too-short"
      : USERNAME_RE.test(formData.username)
        ? "ok"
        : "invalid"

  const usernameStatus: UsernameStatus =
    localValidity === "empty" || localValidity === "too-short"
      ? "idle"
      : localValidity === "invalid"
        ? "invalid"
        : serverCheck?.username !== formData.username
          ? "checking"
          : serverCheck.status === "unknown"
            ? "idle"
            : serverCheck.status

  const usernameReason =
    localValidity === "invalid"
      ? "Use 3–30 characters: letters, numbers, dots or underscores."
      : serverCheck?.username === formData.username
        ? serverCheck.reason
        : null

  /* Prefill the email so the review step is not blank. */
  useEffect(() => {
    if (!isOpen) return

    let active = true
    const controller = new AbortController()

    fetch("/api/auth/me", { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data?.user?.email) {
          setFormData((prev) => ({ ...prev, email: data.user.email ?? "" }))
        }
      })
      .catch(() => {})

    return () => {
      active = false
      controller.abort()
    }
  }, [isOpen])

  /* Debounced availability check against the server. */
  useEffect(() => {
    if (localValidity !== "ok") return

    const username = formData.username
    const controller = new AbortController()

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/creator/username-available?username=${encodeURIComponent(username)}`,
          { signal: controller.signal }
        )
        const data = await res.json()
        if (controller.signal.aborted) return

        if (!res.ok) {
          // Never claim "available" on a failed check — submit would then fail
          // with a 409 the user was told could not happen.
          setServerCheck({ username, status: "unknown", reason: data.error ?? null })
          return
        }

        setServerCheck({
          username,
          status: data.available ? "available" : "taken",
          reason: data.reason ?? null,
        })
      } catch {
        if (!controller.signal.aborted) {
          setServerCheck({ username, status: "unknown", reason: null })
        }
      }
    }, 450)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [formData.username, localValidity])

  const handleInputChange = (field: string, value: string) => {
    if (field === "username") {
      setFormData((prev) => ({
        ...prev,
        username: value.replace(/[^a-zA-Z0-9_.]/g, "").toLowerCase().slice(0, 30),
      }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }))
    }
  }

  /** Shared gate for Next and Submit, so step 2 cannot be reached with bad input. */
  const validate = useCallback((): boolean => {
    if (!formData.full_name.trim() || !formData.username || !formData.channel_name.trim()) {
      toast.error("Please fill in all fields")
      return false
    }
    if (usernameStatus === "taken") {
      toast.error("That username is taken. Try another.")
      return false
    }
    if (usernameStatus === "invalid") {
      toast.error(usernameReason ?? "That username is not valid")
      return false
    }
    if (usernameStatus === "checking") {
      toast.info("Still checking that username…")
      return false
    }
    return true
  }, [formData, usernameStatus, usernameReason])

  const handleSubmit = async () => {
    if (!validate()) return

    setIsLoading(true)
    try {
      /*
        Same-origin, so the Appwrite session cookie is sent automatically and
        the route reads it server-side. No Authorization header, no CORS, and no
        access token in reach of client JS.
      */
      const res = await fetch("/api/creator/onboard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.username,
          full_name: formData.full_name.trim(),
          channel_name: formData.channel_name.trim(),
        }),
      })

      const data = await res.json().catch(() => ({}))

      if (res.status === 401) {
        toast.error("Your session expired. Please sign in again.")
        router.push("/login")
        return
      }

      if (res.status === 409) {
        // Lost the race, or the availability check was stale. Overwrite the
        // cached check so the field shows the error immediately on step 1.
        setServerCheck({
          username: formData.username,
          status: "taken",
          reason: data.error ?? "That username is already taken.",
        })
        setStep(1)
        toast.error(data.error ?? "That username is already taken")
        return
      }

      if (!res.ok) {
        throw new Error(data.error || "Failed to create profile")
      }

      toast.success("Welcome to Creator Studio!")
      onClose()

      // refresh() before push: the sidebar's isCreator comes from a server
      // component, so without it the user lands in the studio while the nav
      // still shows "Become a Creator".
      router.refresh()
      router.push("/studio/dashboard")
    } catch (e) {
      console.error(e)
      toast.error(errorMessage(e) || "Failed to create profile")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Become a Creator" description="Set up your creator profile to start building." size="md">
      <div className="py-2 space-y-6">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2].map(i => (
            <div key={i} className="flex items-center gap-2">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                step >= i ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                {step > i ? <Check className="w-4 h-4" /> : i}
              </div>
              {i < 2 && <div className={cn("w-12 h-1 rounded-full", step > 1 ? "bg-primary" : "bg-muted")} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <User className="w-5 h-5 text-primary" />
              <span>Your Identity</span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboard-full-name">Full Name</Label>
              <Input
                id="onboard-full-name"
                value={formData.full_name}
                onChange={e => handleInputChange("full_name", e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboard-username">Username</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1.5 text-muted-foreground">@</span>
                <Input
                  id="onboard-username"
                  className={cn("pl-7 pr-10",
                    usernameStatus === "available" && "border-foreground",
                    (usernameStatus === "taken" || usernameStatus === "invalid") && "border-destructive"
                  )}
                  value={formData.username}
                  onChange={e => handleInputChange("username", e.target.value)}
                  placeholder="username"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={usernameStatus === "taken" || usernameStatus === "invalid"}
                  aria-describedby="onboard-username-status"
                />
                <div className="absolute right-3 top-2.5">
                  {usernameStatus === "checking" && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {usernameStatus === "available" && <Check className="w-4 h-4 text-foreground" />}
                  {(usernameStatus === "taken" || usernameStatus === "invalid") && <X className="w-4 h-4 text-destructive" />}
                </div>
              </div>
              <p id="onboard-username-status" aria-live="polite" className="min-h-[1.25rem]">
                {usernameStatus === "available" && (
                  <span className="text-sm text-foreground">That username is available.</span>
                )}
                {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                  <span className="text-sm text-destructive">{usernameReason}</span>
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboard-channel">Channel Name</Label>
              <Input
                id="onboard-channel"
                value={formData.channel_name}
                onChange={e => handleInputChange("channel_name", e.target.value)}
                placeholder="Your channel or brand name"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Globe className="w-5 h-5 text-primary" />
              <span>Confirm & Launch</span>
            </div>
            <div className="rounded-lg border p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{formData.full_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-medium">@{formData.username}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Channel</span><span className="font-medium">{formData.channel_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{formData.email}</span></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Next you&apos;ll pick your site address in Website settings. Logo, bank
              details and regional settings can be added there too.
            </p>
          </div>
        )}

        <div className="flex justify-between pt-2 border-t">
          <Button variant="outline" onClick={() => step === 1 ? onClose() : setStep(1)} disabled={isLoading}>
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step === 1 ? (
            <Button onClick={() => { if (validate()) setStep(2) }}>
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Launch Creator Studio
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
